import { useCallback } from 'react';
import { context, trace } from '@opentelemetry/api';
import { logger } from '@parrot-software/observability';

import { api } from 'config/api';

const trinoLog = logger.forDomain('IAM', { subdomain: 'authentication' });
const tracer = trace.getTracer('@parrot-software/observability');

interface SSOResponse {
  redirectUrl: string;
  expiresIn: number;
}

export interface TrinoSSOOptions {
  /** Path + search within Trino to land on after the callback. Defaults to '/'. */
  redirect_to?: string;
  /**
   * How to open the redirect target:
   * - `'same-tab'`: replaces the current tab (post-login / deep-link flow).
   * - `'new-tab'`: opens in a new tab (default — nav click).
   */
  target?: 'same-tab' | 'new-tab';
}

/**
 * Requests a single-use JWT from the admin backend and redirects the browser
 * to Trino's SSO callback URL.
 *
 * The returned JWT expires in ~5 minutes and is consumed on arrival — never
 * persist it. The `api` axios instance already injects the Bearer token and
 * the OTel `traceparent` header, so traces link automatically with the
 * backend span for `GET /api/v1/auth/sso/trino/`.
 *
 * @returns Callback that triggers the SSO flow with the given options
 *
 * @example
 * const ssoTrino = useTrinoSSO();
 * await ssoTrino({ redirect_to: '/ideas/abc-123', target: 'same-tab' });
 */
export const useTrinoSSO = () => {
  return useCallback(async ({ redirect_to = '/', target = 'new-tab' }: TrinoSSOOptions = {}) => {
    // Open the new tab synchronously BEFORE the async API call so we keep the
    // user-gesture context and avoid popup blockers. We then set its URL once
    // the redirect_url is known.
    const placeholder = target === 'new-tab' ? window.open('about:blank', '_blank') : null;

    const span = tracer.startSpan('IAM.trino_sso');
    const ctx = trace.setSpan(context.active(), span);

    return context.with(ctx, async () => {
      trinoLog.info('trinoSsoAttempt', { 'trino.redirectTo': redirect_to, 'trino.target': target });

      try {
        const { data } = await api.get<SSOResponse>('/v1/auth/sso/trino/', {
          params: { redirect_to },
        });

        const redirectUrl = data?.redirectUrl;
        if (!redirectUrl || typeof redirectUrl !== 'string') {
          throw new Error(`Trino SSO response missing "redirectUrl". Received: ${JSON.stringify(data)}`);
        }

        trinoLog.info('trinoSsoResponseReceived', { 'trino.expiresIn': data.expiresIn });

        if (target === 'new-tab' && placeholder) {
          placeholder.location.href = redirectUrl;
          return;
        }

        if (target === 'new-tab') {
          trinoLog.warn('trinoSsoPopupFallback');
        }

        window.location.href = redirectUrl;
      } catch (error) {
        if (placeholder) placeholder.close();
        trinoLog.error('trinoSsoRequest', {
          'error.kind': (error as any)?.constructor?.name || 'TrinoSSOError',
          'error.message': (error as any)?.message,
          'http.status_code': (error as any)?.response?.status,
        });
        throw error;
      } finally {
        span.end();
      }
    });
  }, []);
};
